/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */

import { FC, useEffect, useRef, useState } from 'react';
import { Checkbox, Col, Form, message, Modal, Row, Typography } from 'antd';
import InviteContactSelectSearch from 'components/InviteContactSelectSearch';
import apiRoutes from 'config/apiRoute';
import apiRequests from 'utils/api';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import SelectSearch from 'components/SelectSearch';
import useRequestSource from 'utils/useRequestSource';
import RequestsSelect from '../RequestSelect';

interface AddContactModalProps {
  visible: null | 'new' | 'resend-contact' | 'resend-pending';
  pendingInvites?: any[] | null;
  onClose: () => void;
  onFinish?: () => void;
  modalData?: any;
  clientUuid?: string;
  projectUuid?: string;
}

const { Text } = Typography;

const AddContactModal: FC<AddContactModalProps> = ({ visible, pendingInvites, onClose, onFinish, modalData, clientUuid, projectUuid }) => {
  useRequestSource('AddContactModal', !!visible);

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [client, setClient] = useState<any>(null);
  const [contact, setContact] = useState<any>(modalData);
  const isNonUser = typeof contact === 'string';

  const [loadingPendingInvites, setLoadingPendingInvites] = useState(false);

  const pendingInvitesRef = useRef(pendingInvites);

  const setCheckboxFromContact = (target: any) => {
    const mainEmails = [] as any[];

    ['leadsie_manage_link', 'login_register'].forEach((el) => {
      if (target[el]) {
        mainEmails.push(el);
      }
    });

    form.setFieldsValue({ main_emails: mainEmails });
  };

  useEffect(() => {
    if (clientUuid) return;
  
    const fetchPendingInvites = async () => {
      if (!client?.customer.uuid) return;
  
      setLoadingPendingInvites(true);
      try {
        const res = await apiRequests.get(`${apiRoutes.CUSTOMER_INVITE}`, {
          'filters[status][]': 'pending',
          'filters[customer.uuid][]': client.customer.uuid,
        });
        pendingInvitesRef.current = res?.data?.data;
      } catch (error) {
        asyncErrorHandler(error);
      } finally {
        setLoadingPendingInvites(false);
      }
    };
  
    if (client) {
      fetchPendingInvites();
    } else {
      pendingInvitesRef.current = null;
    }

    form.setFieldsValue({ requests: undefined });
  }, [client, clientUuid]);
  
  useEffect(() => {
    if (isNonUser && visible === 'new') {
      const currentValues = form.getFieldValue('main_emails') || [];
      
      if (!currentValues.includes('login_register')) {
        form.setFieldsValue({ 
          main_emails: [...currentValues, 'login_register'] 
        });
      }
    }
  }, [isNonUser]);

  useEffect(() => {
    if (contact && visible === 'new') {
      const email = isNonUser ? contact : contact?.email;

      const target = pendingInvitesRef.current?.find((x) => x.email === email);

      if (target) {
        setCheckboxFromContact(target);
      }
    }
  }, [contact]);

  useEffect(() => {
    if (modalData && visible === 'resend-pending') {
      setCheckboxFromContact(modalData);
    }
  }, [modalData]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();

      const allValues = {
        social_media: false, creative_package: false, video_production: false,
        website: false, advertising: false, schedule_call_link: false,
        welcome_kit: false, login_register: false, leadsie_manage_link: false,
      };

      values.main_emails?.forEach((i: keyof typeof allValues) => {
        allValues[i] = true;
      });

      const newData = { ...allValues, leadsie_social_link: allValues.leadsie_manage_link };
      const email = isNonUser ? contact : contact?.email;
      const emailHasPending = pendingInvitesRef.current?.find((x) => x.email === email);

      const requestUuids = values.main_emails?.includes('strict_requests') 
        ? values.requests 
        : [];

      await apiRequests.post(
        visible !== 'resend-pending' && !emailHasPending
          ? apiRoutes.CUSTOMER_INVITE
          : `${apiRoutes.CUSTOMER_INVITE}/${(emailHasPending ?? contact)?.uuid}/resend`,
        {
          ...newData,
          email,
          customer_id: clientUuid ?? client.customer.uuid,
          user_id: visible !== 'resend-pending' ? contact?.user?.uuid : undefined,
          requests: requestUuids
        },
      );
      message.success('Invitation sent.');
      setSaving(false);
      onClose();
      onFinish?.();
    } catch (error) {
      asyncErrorHandler(error);
      setSaving(false);
    }
  };

  const activeProjectUuid = projectUuid || client?.uuid;

  return (
    <Modal
      confirmLoading={saving}
      okText="Save"
      onOk={handleSave}
      title={<Text className="primary">Invite contact</Text>}
      visible={!!visible}
      onCancel={() => {
        onClose();
        form.resetFields();
        setContact(null);
      }}
      okButtonProps={{ disabled: (!contact && visible === 'new') || loadingPendingInvites }}
    >
      {visible === 'new' && (
        <Form 
          form={form} 
          layout="vertical" 
          initialValues={{ contacts: isNonUser ? contact : undefined }}
        >
           {
            !clientUuid && (
              <SelectSearch
                allowClear
                label="Select client:"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                placeholder="Select a client"
                api={apiRoutes.PROJECT_TIME_REPORT}
                defaultParams={{ 
                  'page[size]': 1000,
                  'page[number]': 1,
                  'filters[projects.status][]': ['active', 'paused', 'waiting_to_launch', 'in_progress']
                }}
                labelKey={(el) => el?.customer?.business_name || 'N/A'}
                onChange={(selected) => {
                  setClient(selected ?? null); 
                }}
                disableApiSearch
                customSearchField="customer.business_name"
                searchByLabelKey 
                searchKey="search_term"
              />
            )
           }

          <InviteContactSelectSearch
            name="contacts"
            api={apiRoutes.CONTACTS}
            label="Select contact:"
            placeholder="Select a contact"
            labelKeys={['user.name', 'email']}
            defaultParams={{
              'sort[by]': 'name',
              'filters[billing_contacts.status][]': 'active',
            }}
            autoFocus={!!clientUuid}
            allowClear
            render={(el: any) => {
              return (
                <Row justify="space-between">
                  <Text>{el?.name}</Text>
                  <Text className="primary">{el?.email}</Text>
                </Row>
              );
            }}
            onInviteSelect={(value: string) => {
              form.setFieldsValue({ contacts: value });
              setContact(value);
            }}
            onChange={(value: any) => {
              setContact(value);
            }}
          />
        </Form>
      )}
      <Form colon={false} layout="vertical" form={form}>
        <Form.Item label="Include:" name="main_emails">
          <Checkbox.Group 
            style={{ width: '100%' }}
            onChange={(checkedValues) => {
              if (!checkedValues.includes('strict_requests')) {
                form.setFieldsValue({ requests: undefined });
              }
            }}
          >
            <Row gutter={[12, 12]}>
              <Col>
                <Checkbox value="strict_requests">Restrict request access</Checkbox>
              </Col>
              <Col>
                <Checkbox value="leadsie_manage_link">Leadsie</Checkbox>
              </Col>
              <Col>
                <Checkbox disabled={isNonUser} value="login_register">
                  Login/Register
                </Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.main_emails !== currentValues.main_emails}>
          {({ getFieldValue }) => {
            const mainEmails = getFieldValue('main_emails') || [];
            const isStrictSelected = mainEmails.includes('strict_requests');

            return isStrictSelected ? (
              <Form.Item label="Request access" name="requests">
                <RequestsSelect projectUuid={activeProjectUuid} />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddContactModal;
